import React, { useRef } from 'react';

import classnames from 'classnames';

import HorizontalScroller from 'components/uiLibrary/HorizontalScroller';

import { SECTIONS, COMPONENTS } from 'components/Globals/Analytics/constants';
import EntityDisplayCard, { VARIANTS } from 'components/Globals/EntityDisplayCard';
import { THEMES } from 'components/Globals/Layout/ThemeProvider';
// eslint-disable-next-line import/no-cycle
import SectionBlock from 'components/Globals/SectionBlock';
import Loader from 'components/Loader';

import { TP } from 'constants/index';
import { routesWithTitle } from 'constants/routesWithTitle';

import useDeviceTypeLayouts from 'utils/hooks/useDeviceTypeLayouts';
import usePageContext from 'utils/hooks/usePageContext';
import { useQuery } from 'utils/react-query';

import queries from 'containers/Artist/queries';

import { useTranslation } from 'src/i18n';

import ClaimProfileCard from '../ClaimProfileCard';

import { useResponsiveCount } from './hooks/useResponsiveCount';
import classes from './PeopleAlsoViewed.module.scss';

const trackingData = {
  section: SECTIONS.PEOPLE_ALSO_VIEWED,
  component: COMPONENTS.ARTIST_CARD,
};

const seeAlltrackingData = {
  ...trackingData,
  component: COMPONENTS.SEE_ALL_CTA,
};

const PeopleAlsoViewedList = ({
  entityType,
  data,
  cardVariant,
  containerRef,
  isPaidEntity,
  displayCount,
  isMobile,
  theme = THEMES.LIGHT,
  isGrid = false,
  isOverflowed = false,
}) => (
  <div className={isGrid ? classes.peopleAlsoViewedListGrid : classes.peopleAlsoViewedListRow} ref={containerRef}>
    {data?.slice(0, displayCount ?? data?.length)?.map(artist => (
      <div className={!isGrid ? classes.peopleAlsoViewedListRow__card : undefined}>
        <EntityDisplayCard
          key={artist?.id}
          entity={artist}
          entityType={entityType}
          variant={cardVariant}
          trackingData={{
            ...trackingData,
            ...(artist?.hierarchy && {
              meta: {
                criteria: artist?.hierarchy,
              },
            }),
          }}
        />
      </div>
    ))}
    {(() => {
      if (isPaidEntity) {
        return null;
      }

      if (isGrid) {
        return (
          <div className={classes.gridItem}>
            <ClaimProfileCard isVertical variant={theme} />
          </div>
        );
      }

      if (isMobile) {
        return null;
      }

      return (
        <div className={classes.claimProfileCardWrapper}>
          <ClaimProfileCard isVertical variant={theme} fullWidth={isOverflowed} />
        </div>
      );
    })()}
  </div>
);

const PeopleAlsoViewed = ({ entity, entityType, sectionProps = {}, cardVariant = VARIANTS.PORTRAIT }) => {
  const { t } = useTranslation('NS_ENTITY_STUB_PAGE');
  const { activeTabKey } = usePageContext();
  const { isMobile } = useDeviceTypeLayouts();
  const containerRef = useRef(null);
  const { count, isOverflowed } = useResponsiveCount({ containerRef });
  const isOverview = activeTabKey === '';
  const isPaidEntity = entity?.stats?.isPro;

  const { data, isLoading } = useQuery(
    queries.getArtistsWithSimilarProfessions({
      id: entity?.id,
      limit: 10,
      filters: {
        enable_ml: true,
      },
      queryConfig: {
        enabled: !!entity?.id,
      },
    }),
  );

  if (isLoading) {
    return (
      <div className={classes.root}>
        <Loader />
      </div>
    );
  }

  if (!data?.data?.length) {
    return null;
  }

  return (
    <SectionBlock
      title={t(`${TP}.FN_PEOPLE_ALSO_VIEWED`)}
      seeAllTrackingData={seeAlltrackingData}
      linkProps={{
        href: routesWithTitle.ARTISTS.as,
        title: t(`${TP}.VIEW_ALL`),
      }}
      styles={{
        title: classnames(classes.title, { [classes.titleDark]: sectionProps?.theme === THEMES.DARK }),
        seeAllLink: classes.seeAllLink,
        leftBoxIcon: classes.leftBoxIcon,
      }}
      {...sectionProps}
    >
      <div className={classes.container}>
        <div className={isOverview ? classes.peopleAlsoViewedWrapperRow : classes.peopleAlsoViewedWrapperGrid}>
          {!isOverview && !isMobile ? (
            <PeopleAlsoViewedList
              isGrid
              theme={sectionProps?.theme ?? THEMES.LIGHT}
              entityType={entityType}
              isPaidEntity={isPaidEntity}
              data={data?.data}
              cardVariant={cardVariant}
              displayCount={8}
            />
          ) : (
            <HorizontalScroller className={classes.horizontalScrollItems} hideArrow>
              <PeopleAlsoViewedList
                theme={sectionProps?.theme ?? THEMES.LIGHT}
                containerRef={containerRef}
                entityType={entityType}
                data={data?.data}
                cardVariant={cardVariant}
                isPaidEntity={isPaidEntity}
                displayCount={count}
                isMobile={isMobile}
                isOverflowed={isOverflowed}
              />
            </HorizontalScroller>
          )}
        </div>

        {isMobile && !isPaidEntity && <ClaimProfileCard isMobile variant="dark" />}
      </div>
    </SectionBlock>
  );
};

export default PeopleAlsoViewed;
